import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <div className="pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-6 bg-blue-500/10 border border-blue-500/20 px-5 py-2 rounded-full">
              <i className="ri-shield-check-line text-blue-600"></i>
              <span className="text-blue-700 font-semibold text-sm tracking-wider uppercase">Legal</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Privacy <span className="bg-gradient-to-r from-red-600 to-blue-600 bg-clip-text text-transparent">Policy</span>
            </h1>
            <p className="text-lg text-gray-600">Last updated: January 2024</p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-gray-100 mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-information-line text-blue-600"></i>
                Introduction
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                At Madina Associates, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
              </p>
              <p className="text-gray-600 leading-relaxed">
                By accessing or using our services, you agree to the terms outlined in this Privacy Policy. If you do not agree with our policies and practices, please do not use our services.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-gray-100 mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-file-list-line text-blue-600"></i>
                Information We Collect
              </h2>
              <h3 className="text-xl font-bold text-gray-900 mb-4 mt-8">Personal Information</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                We may collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>Contact us through our website forms or email</span>
                </li>
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>Request information about our properties or services</span>
                </li>
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>Schedule property viewings or consultations</span>
                </li>
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>Subscribe to our newsletter or marketing communications</span>
                </li>
              </ul>

              <h3 className="text-xl font-bold text-gray-900 mb-4 mt-8">Automatically Collected Information</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                When you visit our website, we may automatically collect certain information about your device and browsing actions, including:
              </p>
              <ul className="space-y-3">
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>IP address and browser type</span>
                </li>
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>Operating system and device information</span>
                </li>
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>Pages visited and time spent on our website</span>
                </li>
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>Referring website addresses</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-gray-100 mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-settings-line text-blue-600"></i>
                How We Use Your Information
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We use the information we collect for various purposes, including:
              </p>
              <ul className="space-y-3">
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>Responding to your inquiries and providing customer service</span>
                </li>
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>Processing and managing property transactions</span>
                </li>
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>Sending you marketing communications and property updates</span>
                </li>
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>Improving our website and services</span>
                </li>
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>Complying with legal obligations and protecting our rights</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-gray-100 mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-share-line text-blue-600"></i>
                Information Sharing
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="space-y-3">
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-shield-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>With service providers who assist us in operating our business</span>
                </li>
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-shield-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>When required by law or to protect our legal rights</span>
                </li>
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-shield-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>In connection with a business transfer or merger</span>
                </li>
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-shield-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>With your explicit consent</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-gray-100 mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-lock-line text-blue-600"></i>
                Data Security
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-gray-100 mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-user-settings-line text-blue-600"></i>
                Your Rights
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="space-y-3">
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>Access and receive a copy of your personal information</span>
                </li>
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>Request correction of inaccurate information</span>
                </li>
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>Request deletion of your personal information</span>
                </li>
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>Opt-out of marketing communications</span>
                </li>
                <li className="text-gray-600 flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span>Object to processing of your information</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-red-50 rounded-2xl p-8 md:p-12 border-2 border-blue-100">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-mail-line text-blue-600"></i>
                Contact Us
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us at:
              </p>
              <div className="space-y-3">
                <p className="text-gray-900 font-semibold flex items-center gap-3">
                  <i className="ri-mail-fill text-blue-600"></i>
                  info@madinaassociates.com
                </p>
                <p className="text-gray-900 font-semibold flex items-center gap-3">
                  <i className="ri-phone-fill text-blue-600"></i>
                  +1 (555) 123-4567
                </p>
                <p className="text-gray-900 font-semibold flex items-start gap-3">
                  <i className="ri-map-pin-fill text-blue-600 mt-1"></i>
                  <span>123 Business Avenue, Suite 500<br />Metropolitan City, MC 12345</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
